// @ts-strict-ignore
import React, { useMemo } from 'react'
import { useAtomValue } from 'jotai'
import queryString from 'query-string'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useAuth, useFeatureFlags } from '@node-space/hooks'
import { Box, Tab, Tabs } from '@node-space/ui-components'
import { WalletContextProvider } from 'contexts/walletContext'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useWalletQuery } from 'hooks/queries'
import { useBulkCapabiltiesQuery } from 'hooks/queries/Capabilities/useCapabiltiesQuery'
import { useIsActiveWallet } from 'hooks/useActiveWallets'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { ReceiveCrypto } from 'pages/Channels/ReceiveCrypto'
import { customerModelAtom } from 'pages/Customers/atoms/customersAtoms'
import { CustomerModels } from 'pages/Customers/helpers/types'
import { BulkCapabilityRequest } from 'types/capabilities/capabilities'
import { CustomerWalletDetails, CustomerWalletStatusType } from 'types/customers'
import { WalletType } from 'types/types'
import { getWalletActionCapabilityRequestFilters } from 'utils/capabilities'
import { getFiatWalletActionSupport } from 'utils/wallets/fiat'
import { ExtendedDetails } from './components/ExtendedDetails/ExtendedDetails'
import { RecentActivity } from './components/RecentActivity/RecentActivity'
import { WalletDetailsHeader } from './components/WalletDetailsHeader/WalletDetailsHeader'
import { WalletNotFound } from './components/WalletNotFound/WalletNotFound'

const WALLET_PAGE_TABS = {
  RECENT_ACTIVITY: 'recent-activity',
  EXTENDED_DETAILS: 'extended-details',
  RECEIVE_CRYPTO: 'receive-crypto',
} as const
const VALID_WALLET_PAGE_TABS = Object.values(WALLET_PAGE_TABS)
type TabQuery = (typeof WALLET_PAGE_TABS)[keyof typeof WALLET_PAGE_TABS]
const DEFAULT_TAB = 'recent-activity'

interface WalletDetailsProps {
  customerWalletDetails?: CustomerWalletDetails
}

export const WalletDetails = ({ customerWalletDetails }: WalletDetailsProps) => {
  const { t } = useTranslation()
  const {
    enableFiatCurrencyFilter,
    enableWalletDetailsTab,
    enableUsLaunchRestrictions,
    enableFiatPayinCapabilityV2,
    enableFiatPayoutCapabilityV2,
  } = useFeatureFlags()

  const {
    currentAccount,
    isLoading: isLoadingCurrentAccount,
    accountCapabilities,
  } = useAccountsContext()
  const { isEpcWalletPayinsCapabilityEnabled } = accountCapabilities || {}
  const { isAuthed } = useAuth()
  const { id }: { id?: string } = useParams()

  const customerModel = useAtomValue(customerModelAtom)
  const isEmbeddedOrRelianceCustomerModel =
    customerModel === CustomerModels.EMBEDDED || customerModel === CustomerModels.RELIANCE
  const { tab } = queryString.parse(globalThis.location.search) as { tab?: TabQuery }

  const walletId = useMemo(
    () => customerWalletDetails?.legacyWalletId ?? id,
    [customerWalletDetails?.legacyWalletId, id]
  )

  const { data: walletData, isPending, isError } = useWalletQuery(Number(walletId), !!walletId)
  const permissions = useMappedRoles()?.wallets
  const isSelectedWalletActive = useIsActiveWallet(walletData)

  const fiatWalletCapabilitiesRequest: BulkCapabilityRequest = useMemo(() => {
    if (!!walletData?.lsid && currentAccount?.reference && isAuthed && !isLoadingCurrentAccount) {
      return getWalletActionCapabilityRequestFilters(
        [walletData?.lsid],
        enableFiatPayinCapabilityV2,
        enableFiatPayoutCapabilityV2
      )
    }
  }, [walletData, currentAccount?.reference, isAuthed, isLoadingCurrentAccount])

  const { data: bulkCapabilities, isFetching: isFetchingWalletCapabilities } =
    useBulkCapabiltiesQuery(
      fiatWalletCapabilitiesRequest,
      !!fiatWalletCapabilitiesRequest,
      'walletDetailsCapabilitiesCheck'
    )

  const isLoading = isPending || isFetchingWalletCapabilities

  const wallet: WalletType = useMemo(() => {
    const walletProperties = {
      currency: walletData?.currency?.code,
      supportsWithdrawals: walletData?.supportsWithdrawals,
      supportsThirdParty: walletData?.supportsThirdParty,
      supportsDeposits: walletData?.supportsDeposits,
      lsid: walletData?.lsid,
      isLegacyWallet: !walletData?.supportedTransferDestinations?.length,
    }

    const { supportsPay, supportsDeposits, supportsWithdrawals, supportsInternalTransfers } =
      getFiatWalletActionSupport({
        walletProperties,
        bulkCapabilities,
        enableFiatCurrencyFilter,
        enableFiatPayinCapabilityV2,
        enableFiatPayoutCapabilityV2,
      })

    return {
      ...walletData,
      supportsDeposits: supportsDeposits,
      supportsWithdrawals: supportsWithdrawals,
      supportsThirdParty: supportsPay,
      supportsInternalTransfers: supportsInternalTransfers,
    }
  }, [bulkCapabilities, walletData])

  const canEdit = permissions?.canEdit
  const canTrade = permissions?.canTrade
  const isFiatWallet = wallet?.currency?.fiat

  const showWalletDetailsHeader = (canEdit || canTrade) && !customerWalletDetails
  const isExtendedWalletDetailsAvailable =
    (isFiatWallet && wallet?.supportsDeposits) || (!isFiatWallet && !wallet?.supportsThirdParty)

  const isReadOnlyCustomerWallet =
    !!customerWalletDetails &&
    (customerWalletDetails.status === CustomerWalletStatusType.INACTIVE ||
      customerWalletDetails.status === CustomerWalletStatusType.TERMINATED)

  const showTabs = isExtendedWalletDetailsAvailable || isReadOnlyCustomerWallet

  const isWalletDetailsTabVisible = useMemo(() => {
    if (isReadOnlyCustomerWallet) {
      return false
    }

    const isVisible =
      isExtendedWalletDetailsAvailable &&
      isSelectedWalletActive &&
      (isFiatWallet || (enableWalletDetailsTab && !isFiatWallet))

    if (customerWalletDetails && isEmbeddedOrRelianceCustomerModel) {
      return isEmbeddedOrRelianceCustomerModel && isEpcWalletPayinsCapabilityEnabled
    }

    return isVisible
  }, [
    isExtendedWalletDetailsAvailable,
    isSelectedWalletActive,
    isFiatWallet,
    enableWalletDetailsTab,
    isEmbeddedOrRelianceCustomerModel,
    customerWalletDetails,
    isEpcWalletPayinsCapabilityEnabled,
    isReadOnlyCustomerWallet,
  ])

  const isReceiveCryptoTabVisible = useMemo(() => {
    const isVisible =
      isExtendedWalletDetailsAvailable && isSelectedWalletActive && !enableUsLaunchRestrictions

    if (customerWalletDetails && isEmbeddedOrRelianceCustomerModel) {
      return isVisible && isEpcWalletPayinsCapabilityEnabled
    }

    return isVisible
  }, [
    customerWalletDetails,
    isExtendedWalletDetailsAvailable,
    isSelectedWalletActive,
    enableUsLaunchRestrictions,
    isEmbeddedOrRelianceCustomerModel,
    isEpcWalletPayinsCapabilityEnabled,
  ])

  const selectedTab = useMemo(() => {
    if (VALID_WALLET_PAGE_TABS.includes(tab)) {
      return tab
    }

    return DEFAULT_TAB
  }, [tab, DEFAULT_TAB, VALID_WALLET_PAGE_TABS])


  const tabs: Tab[] = [
    {
      id: 'recent-activity',
      header: t('wallets.transactionHistory'),
      content: <RecentActivity isLoading={isLoading} wallet={wallet} />,
      testId: 'wallet-details-transaction-history-tab',
    },
  ]

  if (isWalletDetailsTabVisible && !isPending && isFiatWallet) {
    const walletDetailsTab = {
      id: 'extended-details',
      header: t('walletDetails'),
      content: <ExtendedDetails />,
      testId: 'wallet-details-extended-details-tab',
    }
    tabs.push(walletDetailsTab)
  }

  if (isReceiveCryptoTabVisible) {
    const receiveCryptoTab = {
      id: 'receive-crypto',
      header: t('receiveCrypto'),
      content: <ReceiveCrypto />,
      testId: 'wallet-details-receive-crypto-tab',
    }
    tabs.push(receiveCryptoTab)
  }

  return (
    <WalletContextProvider wallet={wallet}>
      <Box>
        {isError && <WalletNotFound />}

        {showWalletDetailsHeader && !isError && <WalletDetailsHeader isLoading={isLoading} />}

        {!isError && !showTabs && (
          <RecentActivity
            isLoading={isLoading}
            wallet={wallet}
            customerWallet={customerWalletDetails}
          />
        )}

        {!isError && showTabs && <Tabs tabs={tabs} defaultTabId={selectedTab} />}
      </Box>
    </WalletContextProvider>
  )
}
